import { OccType, SourceChannelType } from '@/config/constant';
import BaseService from '@/api/checkInBase';
import DateJS, {
  format2DateStr,
  format2FullTimeStr,
  format2TimeStamp,
  format2TimeStr,
} from '@/utils/date';
import {
  ConfigRoomPrice,
  RentRoomInfo,
  RentTimeInfo,
  StrategyItem,
  SourceChannelInfo,
  CouponValue,
} from '@/redux/reducers/reservation/base.d';
import {
  ReducerState,
  MainState,
  CommonInfo,
  storeName,
} from '@/redux/reducers/reservation/renewal';

import { RenewalCreateAction } from './constant';
import {
  createActionMiddleware,
  getRentTimeInfo,
  getDayDepartureTime,
  flatPriceList,
  getBusinessDate,
  getChannelList,
  getRentDayArr,
  getEmptyCouponData,
} from './helper';

// getState函数覆写
const actionWrapper = createActionMiddleware<ReducerState>(storeName);

// 获取房间定价
const getRoomPriceList: (state: ReducerState) => Promise<ConfigRoomPrice[]> = async (state) => {
  let result: ConfigRoomPrice[] = [];
  try {
    const { channelInfo, commonInfo, rentRoomInfos } = state;
    if (rentRoomInfos.length && channelInfo.channelId) {
      const timeInfo = getRentTimeInfo(state, commonInfo.occType);
      const roomTypeIds: number[] = []; // 去重的房型列表
      const typeIdMap: { [key: number]: boolean } = {}; // 房型id map
      (rentRoomInfos || []).forEach((item) => {
        const { roomTypeId } = item;
        // 已有的房型id
        if (typeIdMap[roomTypeId]) return;
        roomTypeIds.push(roomTypeId);
        typeIdMap[roomTypeId] = true;
      });
      const res = await BaseService.getRoomPrice({
        channelId: channelInfo.channelId,
        endDate: format2DateStr(timeInfo.expectedDepartureTime),
        occDuration: commonInfo.occType === OccType.DayUse ? undefined : timeInfo.rentTime, // 日租房不传
        occType: commonInfo.occType,
        roomTypeIds,
        startDate: format2DateStr(timeInfo.businessDate), // 开始日期要传营业日 营业日小于等于当前日期
        strategyId: channelInfo.strategyId,
        wyuid: commonInfo.ownerWYId,
        memberLevel: commonInfo.memberLevel,
      });
      result = res.data || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

// 获取可选策略
const getStrategyList: (state: ReducerState) => Promise<StrategyItem[]> = async (state) => {
  let result: StrategyItem[] = [];
  try {
    const { commonInfo, channelInfo } = state;
    if (channelInfo.channelId) {
      // 已经选择渠道了
      const timeInfo = getRentTimeInfo(state, commonInfo.occType);
      const res = await BaseService.getStrategyList({
        channelId: channelInfo.channelId,
        memberLevel: commonInfo.memberLevel,
        occDuration: timeInfo.rentTime,
        occType: commonInfo.occType,
      });
      result = res.data || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

/**
 * 更新loading状态
 * 加个timer防止重复修改时闪烁
 */
let loadingTimer;
export const setLoadingStatus: (isLoading: boolean) => void = (isLoading) => {
  if (loadingTimer) {
    clearTimeout(loadingTimer);
  }
  return actionWrapper((dispatch, getState) => {
    // 和当前状态不一样
    loadingTimer = setTimeout(() => {
      loadingTimer = null;
      if (getState().isLoading !== isLoading) {
        dispatch({
          type: RenewalCreateAction.setLoadingStatus,
          payload: isLoading,
        });
      }
    });
  });
};

// 更新初始化状态
const setInitStatus = (isInit: boolean) => {
  return { type: RenewalCreateAction.setInitStatus, payload: isInit };
};

// 更新数据变更状态
const setModifyStatus = (isModify: boolean) => {
  return { type: RenewalCreateAction.setModifyStatus, payload: isModify };
};

// 更新策略列表
const setStrategyList = (data: StrategyItem[]) => {
  return {
    type: RenewalCreateAction.setStrategyList,
    payload: data,
  };
};

// 更新房价列表
const setPriceList = (list: ConfigRoomPrice[]) => {
  return {
    type: RenewalCreateAction.setRoomPriceList,
    payload: list,
  };
};

// 更新入住房间信息
const setRentList: (list: RentRoomInfo[]) => void = (list) => {
  return {
    type: RenewalCreateAction.setRentRoomList,
    payload: list,
  };
};

// 更新公共信息
const setCommonData: (data: Partial<CommonInfo>) => void = (data) => {
  return { type: RenewalCreateAction.setCommonData, payload: data };
};

// 更新日租房时间信息
const setDayTimeData: (time: Partial<RentTimeInfo>) => void = (time) => {
  return {
    type: RenewalCreateAction.setDayTimeData,
    payload: time,
  };
};

// 更新钟点房时间信息
const setHourTimeData: (time: Partial<RentTimeInfo>) => void = (time) => {
  return {
    type: RenewalCreateAction.setHourTimeData,
    payload: time,
  };
};

// 更新客源渠道信息
const setChannelData = (data: Partial<SourceChannelInfo>) => {
  return {
    type: RenewalCreateAction.setChannelData,
    payload: data,
  };
};

// 刷新房间定价
const refreshPriceList = () => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { rentRoomInfos } = state;
    // 没有选中房间
    if (!rentRoomInfos.length) return;
    dispatch(setLoadingStatus(true));
    const priceList = await getRoomPriceList(state);
    dispatch(setPriceList(priceList));
    const { cachePriceMap } = flatPriceList(priceList);
    const emptyCouponData = getEmptyCouponData();
    // 更新已选房间房价
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const itemPriceList = roomItem.roomPriceList || [];
          const typePriceMap = cachePriceMap[roomItem.roomTypeId] || {};
          return {
            ...roomItem,
            roomPriceList: itemPriceList.map((dayItem) => {
              return {
                ...dayItem,
                ...emptyCouponData,
                inputPrice: typePriceMap[dayItem.rentDate],
              };
            }),
          };
        }),
      ),
    );
    dispatch(setLoadingStatus(false));
  });
};

// 获取默认所在营业日
const getDefaultBusinessDate: (callback: () => void) => void = (callback) => {
  return actionWrapper((dispatch, getState) => {
    const { dayTypeTimeInfo, hourTypeTimeInfo } = getState();
    dispatch(setLoadingStatus(true));
    const dayExpectedTime = dayTypeTimeInfo.expectedArrivalTime;
    const hourExpectedTime = hourTypeTimeInfo.expectedArrivalTime;
    const queryArr = [dayExpectedTime];
    const isNotSameDate =
      format2FullTimeStr(dayExpectedTime) !== format2FullTimeStr(hourExpectedTime);
    if (isNotSameDate) {
      queryArr.push(hourExpectedTime);
    }
    Promise.all(queryArr.map(getBusinessDate))
      .then(([dayBusinessDate, hourBusinessDate]) => {
        dispatch(
          setDayTimeData({
            businessDate: dayBusinessDate,
            expectedDepartureTime: getDayDepartureTime(
              { ...dayTypeTimeInfo, businessDate: dayBusinessDate },
              format2TimeStr(dayTypeTimeInfo.expectedDepartureTime),
            ),
          }),
        );
        dispatch(
          setHourTimeData({
            businessDate: isNotSameDate ? hourBusinessDate : dayBusinessDate,
          }),
        );
      })
      .then(() => {
        dispatch(setLoadingStatus(false));
        if (typeof callback === 'function') {
          callback();
        }
      });
  });
};

// 入住调整 更新房间入住日期 清空房价、优惠券
const updateRoomRentDate = () => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { commonInfo, rentRoomInfos } = state;
    const { occType } = commonInfo;
    const rentTimeInfo = getRentTimeInfo(state, occType);
    const rentDateArr = getRentDayArr({ rentTimeInfo, occType });
    const emptyCouponData = getEmptyCouponData();
    dispatch(
      setRentList(
        rentRoomInfos.map((item) => {
          return {
            ...item,
            roomPriceList: rentDateArr.map((dayInfo) => {
              return {
                rentDateId: dayInfo.rentDateId,
                rentDate: dayInfo.rentDate,
                occType,
                inputPrice: '',
                ...emptyCouponData,
              };
            }),
          };
        }),
      ),
    );
  });
};

// 页面初始化
export const initPageState: (initFunc: () => Promise<MainState>) => void = (initFunc) => {
  return async (dispatch) => {
    try {
      // 未初始化状态
      dispatch({
        type: RenewalCreateAction.initDefaultState,
        payload: <ReducerState>{
          isInit: false,
          isLoading: true,
          isModify: false,
          allChannelList: [],
          strategyList: [],
          roomPriceList: [],
          rentRoomInfos: [],
          commonInfo: {},
          allGuestList: [],
          dayTypeTimeInfo: {},
          hourTypeTimeInfo: {},
          channelInfo: {},
        },
      });
      // 获取渠道信息
      getChannelList().then((channelList) => {
        dispatch({ type: RenewalCreateAction.setAllChannelList, payload: channelList });
      });
      // 调用初始化方法
      const initState = await initFunc();
      // 设置初始化数据
      dispatch({ type: RenewalCreateAction.initDefaultState, payload: initState });
      dispatch(setInitStatus(true));
      // 初始化所在营业日
      dispatch(
        getDefaultBusinessDate(() => {
          // 更新入住日期
          dispatch(updateRoomRentDate());
          // 刷新房间定价
          dispatch(refreshPriceList());
        }),
      );
    } catch (e) {
      dispatch(setInitStatus(false));
    }
  };
};

// 根据策略更新日租房离店时间
const setDayETDByStrategy: (
  strategy: StrategyItem,
  callback?: (isChange: boolean) => void,
) => void = (strategy, callback) => {
  return actionWrapper((dispatch, getState) => {
    const { dayTypeTimeInfo } = getState();
    let isETDChange = false;
    if (strategy?.departureDate) {
      // 根据策略的离店时间 计算日租房的离店时间
      const departureTime = getDayDepartureTime(dayTypeTimeInfo, strategy.departureDate);
      isETDChange =
        format2TimeStamp(departureTime) !== format2TimeStamp(dayTypeTimeInfo.expectedDepartureTime); // 离店时间改变
      if (isETDChange) {
        // 更新离店时间
        dispatch(setDayTimeData({ expectedDepartureTime: departureTime }));
      }
    }
    if (typeof callback === 'function') {
      callback(isETDChange);
    }
  });
};

// 入住类型改变
export const handleOccTypeChange: (occType: OccType) => void = (occType) => {
  return actionWrapper(async (dispatch, getState) => {
    const { channelInfo } = getState();
    dispatch(setCommonData({ occType })); // 设置occType
    if (channelInfo.channelId) {
      dispatch(setLoadingStatus(true));
      // 更新计价策略
      const strategyList = await getStrategyList(getState());
      dispatch(setStrategyList(strategyList));
      // 默认选中第一个策略
      const defaultStrategy = strategyList[0];
      dispatch(setChannelData({ strategyId: defaultStrategy?.id }));
      if (occType === OccType.DayUse) {
        // 根据策略变更离店时间
        dispatch(setDayETDByStrategy(defaultStrategy));
      }
      dispatch(setLoadingStatus(false));
    }
    dispatch(updateRoomRentDate()); // 更新入住日期
    dispatch(refreshPriceList()); // 刷新房价
  });
};

// 日租房时间改变
export const handleDayTimeChange: (data: Partial<RentTimeInfo>) => void = (data) => {
  return actionWrapper(async (dispatch, getState) => {
    const { dayTypeTimeInfo } = getState();
    const updateDate: RentTimeInfo = { ...dayTypeTimeInfo, ...data };
    if (
      data.expectedArrivalTime &&
      data.expectedArrivalTime !== dayTypeTimeInfo.expectedArrivalTime
    ) {
      // 预抵日期改变 更新所在营业日
      dispatch(setLoadingStatus(true));
      const businessDate = await getBusinessDate(data.expectedArrivalTime);
      updateDate.businessDate = businessDate;
      dispatch(setLoadingStatus(false));
    }
    updateDate.expectedDepartureTime = getDayDepartureTime(
      updateDate,
      format2TimeStr(updateDate.expectedDepartureTime),
    ); // 计算离店时间
    dispatch(setDayTimeData(updateDate)); // 更新时间信息
    dispatch(updateRoomRentDate()); // 更新入住日期
    dispatch(refreshPriceList()); // 刷新可选房间
    dispatch(setModifyStatus(true)); // 数据修改标识
  });
};

// 钟点房时间改变
export const handleHourTimeChange: (data: Partial<RentTimeInfo>) => void = (data) => {
  return actionWrapper(async (dispatch, getState) => {
    const { channelInfo, hourTypeTimeInfo } = getState();
    const updateDate = { ...hourTypeTimeInfo, ...data };
    if (data.expectedArrivalTime) {
      // 预抵时间修改 拉取所在营业日
      dispatch(setLoadingStatus(true));
      const businessDate = await getBusinessDate(data.expectedArrivalTime);
      updateDate.businessDate = businessDate;
      dispatch(setLoadingStatus(false));
    }
    // 抵店时间和入住时长都有 更新离店时间
    if (updateDate.expectedArrivalTime && updateDate.rentTime) {
      updateDate.expectedDepartureTime = DateJS(updateDate.expectedArrivalTime).add(
        updateDate.rentTime,
        'hour',
      );
    }
    dispatch(setHourTimeData(updateDate)); // 更新时间信息
    dispatch(updateRoomRentDate()); // 更新入住日期
    dispatch(setModifyStatus(true)); // 数据修改标识
    if (data.rentTime && channelInfo.channelId) {
      // 入住时长修改 且已选渠道 更新计价策略
      dispatch(setLoadingStatus(true));
      const strategyList = await getStrategyList(getState());
      dispatch(setStrategyList(strategyList));
      // 默认选中第一个策略
      const defaultStrategy = strategyList[0];
      dispatch(setChannelData({ strategyId: defaultStrategy?.id }));
      dispatch(setLoadingStatus(false));
      // 钟点房入住时长改变 刷新房价
      dispatch(refreshPriceList());
    }
  });
};

// 渠道id改变
const handleChannelIdChange = (channelId) => {
  return actionWrapper(async (dispatch, getState) => {
    const { allChannelList, commonInfo } = getState();
    const targetChannel = allChannelList.find((item) => item.id === channelId);
    if (targetChannel && targetChannel.type !== SourceChannelType.OTA) {
      // 非OTA渠道 清空渠道单号 预付款 开票金额
      dispatch(
        setChannelData({
          channelId,
          strategyId: undefined,
          channelOrderNo: '',
          otaPaymentAmount: '',
          invoiceAmount: '',
        }),
      );
    } else {
      // 更新渠道 策略
      dispatch(setChannelData({ channelId, strategyId: undefined }));
    }
    // 更新计价策略
    dispatch(setLoadingStatus(true));
    const state = getState();
    const strategyList = await getStrategyList(state);
    dispatch(setStrategyList(strategyList));
    // 默认选中第一个策略
    const defaultStrategy = strategyList[0];
    dispatch(setChannelData({ channelId, strategyId: defaultStrategy?.id }));
    if (commonInfo.occType === OccType.DayUse) {
      // 根据策略变更离店时间
      dispatch(setDayETDByStrategy(defaultStrategy));
    }
    dispatch(setLoadingStatus(false));
    // 更新房型价格
    dispatch(refreshPriceList());
  });
};

// 策略改变
const handleStrategyChange = (strategyId) => {
  return actionWrapper(async (dispatch, getState) => {
    const { commonInfo, strategyList } = getState();
    dispatch(setChannelData({ strategyId }));
    const selectItem = strategyList.find((item) => item.id === strategyId);
    if (commonInfo.occType === OccType.DayUse) {
      // 根据策略变更离店时间
      dispatch(setDayETDByStrategy(selectItem));
    }
    // 更新房型价格
    dispatch(refreshPriceList());
  });
};

// 客源渠道修改
export const handleChannelDataChange: (data: Partial<SourceChannelInfo>) => void = (data) => {
  return (dispatch) => {
    const { channelId, strategyId } = data;
    if (channelId != null) {
      dispatch(handleChannelIdChange(channelId));
    } else if (strategyId != null) {
      dispatch(handleStrategyChange(strategyId));
    } else {
      dispatch(setChannelData(data));
    }
    dispatch(setModifyStatus(true)); // 数据修改标识
  };
};

// 入住房间修改
export const handleRentItemChange: (indexId: string, updateValue: Partial<RentRoomInfo>) => void = (
  indexId,
  updateValue,
) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    dispatch(
      setRentList(
        rentRoomInfos.map((item) => {
          if (item.indexId === indexId) {
            return {
              ...item,
              ...updateValue,
            };
          }
          return item;
        }),
      ),
    );
  });
};

// 批量修改房间券
export const batchUpdateRoomCoupon = (data: Record<string, Record<string, CouponValue>>) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    // 更新券
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const roomCouponMap = data[roomItem.indexId];
          if (!roomCouponMap) return roomItem;
          const itemPriceList = roomItem.roomPriceList || [];
          return {
            ...roomItem,
            roomPriceList: itemPriceList.map((dayItem) => {
              // 当前间夜券有调整
              if (roomCouponMap[dayItem.rentDateId]) {
                return {
                  ...dayItem,
                  ...roomCouponMap[dayItem.rentDateId],
                };
              }
              return dayItem;
            }),
          };
        }),
      ),
    );
  });
};

// 获取全部状态
export const getMainState: (callback: (state: ReducerState) => void) => void = (callback) => {
  return actionWrapper((_, getState) => {
    try {
      callback(getState());
    } catch (e) {
      //
    }
  });
};
